import type { TravellerCount } from '@itineraries/entities/itinerary';
import type { Chapter } from '@travel-planner/shared/schemas';
import { aggregateTravellers } from '@itineraries/entities/itinerary';
import { useMemo } from 'react';
import { toastStore } from '@/shared/ui';
import {
  useChapterCountries,
  useChapterTravellerTypes,
} from '../api/use-chapter-form-data';
import { useUpdateChapters } from '../api/use-update-chapters';
import { readChapters } from '../model/chapter';
import { useChapterEditorStore } from '../model/use-chapter-editor-store';
import { ChapterEditorModal } from './chapter-editor-modal';

type ChapterEditorConnectorProps = {
  itineraryId: string | null;
  itineraryStartDate: string | null;
  itineraryEndDate: string | null;
  undated: boolean;
  sections: unknown;
  // Itinerary-level travellers jsonb. Surfaces as the "Itinerary default"
  // summary inside the modal so consultants know what their non-custom
  // chapters will inherit from.
  itineraryTravellers: unknown;
  // Read-only historical-version view. The editor still opens (reading a past
  // version's chapters is exactly the point) but saving is blocked.
  isReadOnly?: boolean;
};

// Single mount-point for the chapter editor modal. The "Chapters"
// button in the view-tabs row drives `useChapterEditorStore.open()`;
// this connector owns the mutation lifecycle + toasts, plus the
// form-data queries (countries + traveller types). Side-effect
// toasts live here (not in the api/ hook) per the architecture doc.
export const ChapterEditorConnector = ({
  itineraryId,
  itineraryStartDate,
  itineraryEndDate,
  undated,
  sections,
  itineraryTravellers,
  isReadOnly = false,
}: ChapterEditorConnectorProps) => {
  const isOpen = useChapterEditorStore((s) => s.isOpen);
  const close = useChapterEditorStore((s) => s.close);
  const mutation = useUpdateChapters({ itineraryId });

  // Form-data queries are gated by `isOpen` so they don't fire on
  // every itinerary page load — chapter editing is opt-in.
  const countriesQuery = useChapterCountries({ enabled: isOpen });
  const travellerTypesQuery = useChapterTravellerTypes({ enabled: isOpen });

  const chapters = useMemo(() => readChapters(sections), [sections]);

  const typesById = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of travellerTypesQuery.data?.data ?? []) {
      map.set(t.id, t.title ?? '');
    }
    return map;
  }, [travellerTypesQuery.data]);

  const defaultTravellers: TravellerCount[] = useMemo(
    () => aggregateTravellers(itineraryTravellers, typesById),
    [itineraryTravellers, typesById]
  );

  const travellerTypeOptions = useMemo(
    () =>
      (travellerTypesQuery.data?.data ?? []).map((t) => ({
        id: t.id,
        title: t.title ?? t.id,
      })),
    [travellerTypesQuery.data]
  );

  const countries = countriesQuery.data ?? [];

  const handleSave = (next: Chapter[]) => {
    if (!itineraryId || isReadOnly) return;
    mutation.mutate(
      { id: itineraryId, chapters: next },
      {
        onSuccess: (res) => {
          close();
          const cascade = res.cascadeItemCount ?? 0;
          toastStore.getState().show({
            message:
              cascade > 0
                ? `Chapters saved · travellers cascaded to ${cascade} item${
                    cascade === 1 ? '' : 's'
                  }`
                : 'Chapters saved',
          });
        },
        onError: (err) => {
          toastStore.getState().show({
            message: err.message || 'Could not save chapters',
          });
        },
      }
    );
  };

  return (
    <ChapterEditorModal
      chapters={chapters}
      countries={countries}
      defaultTravellers={defaultTravellers}
      isOpen={isOpen}
      isPending={mutation.isPending}
      isReadOnly={isReadOnly}
      itineraryEndDate={itineraryEndDate}
      itineraryStartDate={itineraryStartDate}
      onClose={close}
      onSave={handleSave}
      travellerTypeOptions={travellerTypeOptions}
      typesById={typesById}
      undated={undated}
    />
  );
};
